import { type AsyncResult, SynchrounousState, type VoidAsyncResult, asVoidAsyncResult } from "@/cache/AsyncResult";
import { networkCaches } from "@/cache/GlobalCache";
import { jetStreamsLayerConfig } from "@/constants/layerConfigAttributes";
import type { CheckedProps, LayerBase, PropsChecker } from "@/layers";
import type { ScenePublicApi } from "@/layers/Compositor";
import { JetStreamColorExpression, normalizeJetStreamsFeatureCollection } from "@/layers/geojson/JetStreamsLayerUtils";
import { MapboxGeoJSONLayer } from "@/layers/geojson/MapboxGeoJSONLayer";
import type { LayerKindName } from "@/layers/utility/createLayerObject";
import { roundToNearestCacheableTime } from "@/time/roundByLayerKind";
import { safeFmt } from "@/utility/safeFmt";
import { getModelBoundingBox } from "@/weather-parameters/lookup";
import type { GenericGeoJsonRequest, GeoJSONFeatureCollection } from "@mm/api.meteomatics.com";
import type { GuiTimeZone, JetStreamsLayer } from "@mm/metx-workbench.meteomatics.com";
import { debounce } from "lodash";
import type { DateTime } from "luxon";
import type { Expression, FilterSpecification, LineLayout, LinePaint } from "mapbox-gl";

export { JetStreamLegendColormap } from "@/layers/geojson/JetStreamsLayerUtils";

export class JetStreamsLayerImpl extends MapboxGeoJSONLayer<JetStreamsLayer> {
  readonly mapboxLineLayerId: string;
  readonly mapboxArrowLayerId: string;
  readonly baseLineFilter: FilterSpecification = ["!=", "_arrow_head", true];
  readonly arrowLineFilter: FilterSpecification = ["==", "_arrow_head", true];
  readonly formattedDataCache = new WeakMap<GeoJSONFeatureCollection, Map<number, GeoJSONFeatureCollection>>();
  private wasAnimationMode = false;

  constructor(id: number, props: JetStreamsLayer, scene: ScenePublicApi, timezone: GuiTimeZone) {
    super(id, props, scene, timezone);

    this.updateMapboxLayoutProperties({
      visibility: "none",
    });

    this.mapboxLineLayerId = `${this.humanReadableId()}-line`;
    this.mapboxArrowLayerId = `${this.humanReadableId()}-arrow`;

    this.scene.getMapboxMap().addLayer({
      id: this.mapboxLineLayerId,
      type: "line",
      source: this.mapbox_source,
      filter: this.baseLineFilter,
      paint: {
        "line-color": JetStreamColorExpression,
        "line-width": this.getLineWidthExpression(this.props.line_width),
        "line-opacity": this.props.opacity,
      },
      layout: {
        "line-cap": "round",
        "line-join": "round",
        visibility: this.props.show ? "visible" : "none",
      },
    });

    this.scene.getMapboxMap().addLayer({
      id: this.mapboxArrowLayerId,
      type: "line",
      source: this.mapbox_source,
      filter: this.arrowLineFilter,
      paint: {
        "line-color": JetStreamColorExpression,
        "line-width": this.getArrowWidthExpression(this.props.line_width),
        "line-opacity": this.props.opacity,
      },
      layout: {
        "line-cap": "round",
        "line-join": "round",
      },
    });

    this.updateArrowVisibility(this.props);
  }

  protected updateMapboxLineLayoutProperties(props?: LineLayout) {
    const entries = Object.entries(props ?? {}) as Array<[keyof LineLayout, LineLayout[keyof LineLayout]]>;
    for (const [name, value] of entries) {
      this.scene.getMapboxMap().setLayoutProperty(this.mapboxLineLayerId, name, value);
    }
  }

  protected updateMapboxLinePaintProperties(props?: LinePaint) {
    const entries = Object.entries(props ?? {}) as Array<[keyof LinePaint, LinePaint[keyof LinePaint]]>;
    for (const [name, value] of entries) {
      this.scene.getMapboxMap().setPaintProperty(this.mapboxLineLayerId, name, value);
    }
  }

  protected updateMapboxArrowLayoutProperties(props?: LineLayout) {
    const entries = Object.entries(props ?? {}) as Array<[keyof LineLayout, LineLayout[keyof LineLayout]]>;
    for (const [name, value] of entries) {
      this.scene.getMapboxMap().setLayoutProperty(this.mapboxArrowLayerId, name, value);
    }
  }

  protected updateMapboxArrowPaintProperties(props?: LinePaint) {
    const entries = Object.entries(props ?? {}) as Array<[keyof LinePaint, LinePaint[keyof LinePaint]]>;
    for (const [name, value] of entries) {
      this.scene.getMapboxMap().setPaintProperty(this.mapboxArrowLayerId, name, value);
    }
  }

  private getLineWidthExpression(baseWidth: number): number | Expression {
    return [
      "interpolate",
      ["linear"],
      ["to-number", ["get", "wind_speed"], 0],
      60,
      Math.max(1, Number(baseWidth) * 0.8),
      240,
      Math.max(1, Number(baseWidth) * 1.8),
    ];
  }

  private getArrowSizeValue(layer: Pick<JetStreamsLayer, "custom_options">): number {
    const raw = Number(layer.custom_options?.arrow_size);
    const value = Number.isFinite(raw) ? raw : jetStreamsLayerConfig.defaultArrowSize;
    return Math.max(
      jetStreamsLayerConfig.arrowSizeLimits.min,
      Math.min(jetStreamsLayerConfig.arrowSizeLimits.max, value),
    );
  }

  private getArrowSizeScale(layer: Pick<JetStreamsLayer, "custom_options">): number {
    return this.getArrowSizeValue(layer) / jetStreamsLayerConfig.defaultArrowSize;
  }

  private getArrowWidthExpression(baseWidth: number): number | Expression {
    // Arrow thickness follows line width; arrow size slider only affects arrowhead geometry.
    return this.getLineWidthExpression(Math.max(1, Number(baseWidth) * 1.05));
  }

  private mapRawToFormattedResult(
    rawResult: AsyncResult<GeoJSONFeatureCollection>,
    layer: Pick<JetStreamsLayer, "custom_options">,
  ): AsyncResult<GeoJSONFeatureCollection> {
    const arrowSizeValue = this.getArrowSizeValue(layer);
    const normalize = (fc: GeoJSONFeatureCollection) => {
      const existingBySize = this.formattedDataCache.get(fc) ?? new Map<number, GeoJSONFeatureCollection>();
      if (!this.formattedDataCache.has(fc)) {
        this.formattedDataCache.set(fc, existingBySize);
      }
      const cached = existingBySize.get(arrowSizeValue);
      if (cached) {
        return cached;
      }
      const normalized = normalizeJetStreamsFeatureCollection(fc, {
        arrowSizeScale: this.getArrowSizeScale(layer),
      });
      existingBySize.set(arrowSizeValue, normalized);
      return normalized;
    };

    const synchronous =
      rawResult.synchronous === SynchrounousState.StillPending ||
      rawResult.synchronous === SynchrounousState.NotRequested ||
      rawResult.synchronous === SynchrounousState.PermanentlyFailed
        ? rawResult.synchronous
        : normalize(rawResult.synchronous);

    return {
      synchronous,
      asynchronous: rawResult.asynchronous.then(normalize),
      cancel: rawResult.cancel,
    };
  }

  private updateArrowVisibility(layer: Pick<JetStreamsLayer, "show" | "custom_options">): void {
    const showArrows = layer.custom_options?.show_arrows ?? jetStreamsLayerConfig.defaultShowArrows;
    const arrowVisibility = layer.show && showArrows ? "visible" : "none";
    this.updateMapboxArrowLayoutProperties({
      visibility: arrowVisibility,
    });
  }

  beforeRender(): void {
    const isAnimationMode = this.scene.isAnimation();
    if (!isAnimationMode && this.wasAnimationMode) {
      networkCaches.geojson_cache.cancelPendingGeoJsonRequests({
        kind: this.props.kind,
        path: "get_jet_streams",
      });
    }
    this.wasAnimationMode = isAnimationMode;

    if (isAnimationMode) {
      this.updateTrigger();
    } else {
      this.debouncedUpdateTrigger();
    }
  }

  debouncedUpdateTrigger = debounce(
    () => {
      this.updateTrigger();
      // Keep periodic updates even during continuous map renders.
    },
    400,
    { leading: true, maxWait: 1000 },
  );

  checker(): PropsChecker<JetStreamsLayer, LayerBase<JetStreamsLayer>> {
    type CheckerProps = CheckedProps<JetStreamsLayer>;
    const checkLayerProp = (property: CheckerProps) => (prev: any, curr: any) => {
      const changed = prev[property] !== curr[property];
      if (changed) {
        this.setLayerProps({ [property]: curr[property] });
        switch (property) {
          case "model":
            this.beforeRender();
            break;
          case "parameter_unit":
            this.beforeRender();
            break;
          case "ens_select":
            this.beforeRender();
            break;
          case "calibrated":
            this.beforeRender();
            break;
          case "show":
            this.updateArrowVisibility(curr);
            this.updateMapboxLineLayoutProperties({ visibility: curr.show ? "visible" : "none" });
            break;
          case "opacity":
            this.updateMapboxLinePaintProperties({
              "line-opacity": curr[property],
            });
            this.updateMapboxArrowPaintProperties({
              "line-opacity": curr[property],
            });
            break;
          case "line_width":
            this.updateMapboxLinePaintProperties({
              "line-width": this.getLineWidthExpression(curr[property]),
            });
            this.updateMapboxArrowPaintProperties({
              "line-width": this.getArrowWidthExpression(curr[property]),
            });
            break;
          case "custom_options":
            this.updateArrowVisibility(curr);
            if (prev.custom_options?.arrow_size !== curr.custom_options?.arrow_size) {
              this.beforeRender();
            }
            break;
          case "legend_visible":
            break;
          default: {
            const _exhaustive: never = property;
            return _exhaustive;
          }
        }
      }
      return changed;
    };
    return {
      legend_visible: checkLayerProp("legend_visible"),
      model: checkLayerProp("model"),
      opacity: checkLayerProp("opacity"),
      parameter_unit: checkLayerProp("parameter_unit"),
      calibrated: checkLayerProp("calibrated"),
      show: checkLayerProp("show"),
      line_width: checkLayerProp("line_width"),
      ens_select: checkLayerProp("ens_select"),
      custom_options: checkLayerProp("custom_options"),
    };
  }

  updateTrigger(): void {
    const dateTimeWithOffset = roundToNearestCacheableTime(
      this.scene.getDisplayTimeWithOffset(),
      this.props.kind as LayerKindName,
    );
    const request = this.createRequest(dateTimeWithOffset);

    const rawResult = networkCaches.geojson_cache.retrieveGeoJsonEndpoint(request, {
      kind: this.props.kind,
      owner: this.getGeoJsonRequestOwner(),
    });
    this.cancelSupersededGeoJsonRequests(rawResult);
    const result = this.mapRawToFormattedResult(rawResult, this.props);
    this.updateSource(result, dateTimeWithOffset, undefined, {
      keepLastDataWhilePending: this.scene.isAnimation(),
    });
  }

  isRebuffering(time: DateTime): boolean {
    return super.isRebuffering(time);
  }

  createRequest(dateTimeWithOffset: DateTime): GenericGeoJsonRequest {
    const { area } = this.computeBoundingBox(0);

    return {
      path: "get_jet_streams",
      area: area,
      boundingBoxLimit: getModelBoundingBox(this.props.model),
      datetime: dateTimeWithOffset,
      query: {
        source: this.props.model,
      },
    };
  }

  fetchData(timeFrame: DateTime): VoidAsyncResult {
    const dateTimeWithOffset = roundToNearestCacheableTime(
      this.scene.getDateTimeWithOffset(timeFrame),
      this.props.kind as LayerKindName,
    );
    const request = this.createRequest(dateTimeWithOffset);

    const result = networkCaches.geojson_cache.retrieveGeoJsonEndpoint(request, {
      kind: this.props.kind,
    });
    return asVoidAsyncResult(result);
  }

  getActiveWeatherParametersAsString(): { model: string; parameter: string }[] {
    return [];
  }

  humanReadableId(): string {
    return safeFmt`metx.jetstreamslayer#${this.uid}#${this.props.model}.${this.props.parameter_unit}`;
  }

  removeLayer() {
    this.debouncedUpdateTrigger.cancel();
    networkCaches.geojson_cache.cancelPendingGeoJsonRequests({
      kind: this.props.kind,
      path: "get_jet_streams",
    });
    this.scene.getMapboxMap().removeLayer(this.mapboxArrowLayerId);
    this.scene.getMapboxMap().removeLayer(this.mapboxLineLayerId);
    super.removeLayer();
  }

  moveZIndex(beforeLayerId?: string) {
    const map = this.scene.getMapboxMap();
    super.moveZIndex(beforeLayerId);
    map.moveLayer(this.mapboxLineLayerId, beforeLayerId);
    map.moveLayer(this.mapboxArrowLayerId, beforeLayerId);
  }

  peekLayerCache(request: GenericGeoJsonRequest): AsyncResult<GeoJSONFeatureCollection> {
    return networkCaches.geojson_cache.peekGeoJsonEndpoint(request, {
      kind: this.props.kind,
    });
  }
}
